import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import OtpPage from "./OtpPage";
import AIInterview from "../AIInterview";
import {
  initSession,
  loadOtpSession,
  selectOtpScreen,
  selectFallbackAt
} from "../../store/slices/otpSlice";
import EscalationPage from "./EscalationPage";
import AvailabilityPage from "./AvailabilityPage";
import ThanksDigitalPage from "./ThanksDigitalPage";
import { DIGITAL_SCREENS } from "../../templates/strings";
import { sendOtpThunk, sessionStatusThunk } from "../../store/thunks/otpThunks";

/**
 * OtpFlow — renders inside <QuestionContainer> in IndexPage.jsx
 * after funnelInfo.leadForm.isSubmitted === true, only for Standard Leadservice.
 *
 * On fresh load:   sends OTP, starts 10-min fallback timer
 * On page refresh: rehydrates from sessionStorage, re-syncs timer from backend,
 *                  does NOT re-send OTP (fallbackAt already set means SMS was sent)
 */
const OtpFlow = () => {
  const { OTP, ESCALATION, AVAILABILITY, THANKS_DIGITAL, THANKS_MANUAL } =
    DIGITAL_SCREENS;
  const dispatch = useDispatch();
  const [otpSession] = useState(() => loadOtpSession());
  // KI-Interview: letzter Schritt nach erfolgreicher Verifizierung, im
  // selben Container wie alle Screens davor — danach erst die Danke-Seite.
  const [interviewDone, setInterviewDone] = useState(false);
  const funnelInfo = useSelector((state) => state.funnelInfo);
  const { funnel } = funnelInfo;
  const leadId = funnelInfo.leadForm.leadId || otpSession?.leadId;
  const phone = funnelInfo.redirectUrlParams.phone || otpSession?.phone;
  const country = funnel.companyInfo?.country || "DE";
  const isAiInterviewAndDossierEnabled =
    funnel.customer?.isAiInterviewAndDossierEnabled ?? false;
  const screen = useSelector(selectOtpScreen);
  const fallbackAt = useSelector(selectFallbackAt);

  useEffect(() => {
    if (!leadId || !phone) return;

    // Always sync leadId/phone into Redux (initSession guards against overwrite
    // if already populated from sessionStorage rehydration)
    dispatch(initSession({ leadId, phone }));

    if (fallbackAt) {
      // Page refresh path
      // sessionStorage had a session — OTP was already sent.
      // Just re-sync the timer from backend to check if 10-min window expired.
      dispatch(sessionStatusThunk({ leadId }));
    } else {
      // Fresh load path
      // No existing session — send OTP for the first time.
      dispatch(sendOtpThunk({ leadId, phone }));
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Empty deps intentional: we only want this to run once on mount,
  // not re-trigger on every Redux state change.

  if (!leadId || !phone) return null;

  switch (screen) {
    case OTP:
      return <OtpPage leadId={leadId} />;
    case ESCALATION:
      return <EscalationPage leadId={leadId} country={country} />;
    case AVAILABILITY:
      return <AvailabilityPage leadId={leadId} />;
    case THANKS_DIGITAL:
      return interviewDone || !isAiInterviewAndDossierEnabled ? (
        <ThanksDigitalPage />
      ) : (
        <AIInterview leadId={leadId} onFinish={() => setInterviewDone(true)} />
      );
    case THANKS_MANUAL:
      // Return null → IndexPage condition re-evaluates → renders <Greet />
      return null;
    default:
      return <OtpPage leadId={leadId} />;
  }
};

export default OtpFlow;
